<script setup lang="ts">
import { computed, onMounted, onUpdated, ref, useAttrs, useSlots, type Component } from 'vue';
import { hueVars, type NxHue } from '../lib/hue';

export type NxBadgeVariant = 'default' | 'neutral' | 'success' | 'danger' | 'warning' | 'info';
/** `soft` = tinted fill + colored text (default). `solid` = full-color fill + on-color text. */
export type NxBadgeEmphasis = 'soft' | 'solid';
export type NxBadgeSize = 'xs' | 's' | 'm' | 'l' | 'xl';

/** The token icon class an icon in a badge of this size takes. */
const ICON_CLASS: Record<NxBadgeSize, string> = {
  xs: 'nxt-icon-xs',
  s: 'nxt-icon-sm',
  m: 'nxt-icon-md',
  l: 'nxt-icon-lg',
  xl: 'nxt-icon-xl',
};

const props = withDefaults(
  defineProps<{
    variant?: NxBadgeVariant;
    /** Defaults to `'soft'`. */
    emphasis?: NxBadgeEmphasis;
    /** Defaults to `'s'`. */
    size?: NxBadgeSize;
    /**
     * Category hue instead of an intent colour — for badges that only tell
     * categories apart (request type, source). Wins over `variant`.
     */
    hue?: NxHue;
    /**
     * Icon before the text, sized for the badge (`nxt-icon-*`) and hidden
     * from assistive tech — the text carries the meaning. For anything else
     * use the `#icon` slot; it gets that size class as `iconClass`.
     */
    icon?: Component;
    /**
     * Cut a text wider than its container with an ellipsis instead of
     * overflowing it, on one line; the full text becomes the tooltip (unless
     * the consumer passes its own `title`). For badge rows in narrow columns —
     * give the row a shrinking `NxChipGroup` (`shrink`). Text badges only: a
     * badge with an icon lays out as a flex row and is not cut.
     */
    truncate?: boolean;
    /**
     * Centre the text optically for content of capitals and digits, such as
     * codes and numbers: the font's line box leaves a little more room below
     * the baseline than above the cap height, so such text reads set too high.
     * This adds a hairline at the top. Defaults to `false` (unchanged badge).
     */
    capCentered?: boolean;
  }>(),
  {
    variant: 'default',
    emphasis: 'soft',
    size: 's',
    hue: undefined,
    icon: undefined,
    truncate: false,
    capCentered: false,
  }
);

const slots = useSlots();

const hueStyle = computed(() => hueVars(props.hue));
const iconClass = computed(() => ICON_CLASS[props.size]);
const hasIcon = computed(() => !!props.icon || !!slots.icon);

// The tooltip of a truncating badge is its own text, read from the DOM so any
// slot content works; a consumer's `title` (a fallthrough attribute) wins.
const attrs = useAttrs();
const root = ref<HTMLElement | null>(null);
const autoTitle = ref<string | undefined>(undefined);
function syncTitle() {
  autoTitle.value =
    props.truncate && attrs.title == null
      ? root.value?.textContent?.trim() || undefined
      : undefined;
}
onMounted(syncTitle);
onUpdated(syncTitle);
</script>

<template>
  <span
    ref="root"
    class="nx-badge"
    :class="[
      `variant-${props.variant}`,
      `emphasis-${props.emphasis}`,
      `height-${props.size}`,
      {
        'has-hue': !!props.hue,
        'has-icon': hasIcon,
        'is-truncate': props.truncate && !hasIcon,
        'is-cap-centered': props.capCentered,
      },
    ]"
    :style="hueStyle"
    :title="autoTitle"
  >
    <slot name="icon" :icon-class="iconClass">
      <component
        :is="props.icon"
        v-if="props.icon"
        class="nx-badge__icon"
        :class="iconClass"
        aria-hidden="true"
      />
    </slot>
    <slot />
  </span>
</template>

<style scoped>
/* NxBadge consumes the --nxt-badge-* component tokens (I-596). The base rule
   reads only those; each variant/emphasis combo re-points bg/fg/border at the
   matching semantic role family (brand/success/danger/warning/info), subtle
   for `soft` and solid for `solid` — mirroring the NxButton pilot. */
.nx-badge {
  display: inline-block;
  width: max-content;
  font-weight: var(--nxt-font-weight-semibold);
  /* The shape follows the design's badges axis (data-nx-badges, I-2510):
     unset (`square`) these are the small radius and the hairline border. */
  border-radius: var(--nxd-badge-radius, var(--nxt-radius-sm));
  padding-inline: var(--nxd-badge-padding, var(--nxt-space-xs));
  font-size: var(--nxt-badge-size);
  border: var(--nxt-hairline) solid var(--nxd-badge-border-color, var(--nxt-badge-border));
  background: var(--nxt-badge-bg);
  color: var(--nxt-badge-fg);
}

/* `truncate`: one line, cut with an ellipsis at the width of the container
   (I-2382, K2: badge rows in narrow columns, I-2566). */
.nx-badge.is-truncate {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* `capCentered`: in the UI font the line box leaves a little more room below
   the baseline than above the cap height (measured: 0.23em above, 0.25em
   below), so capitals and digits read set too high. A hairline more at the
   top evens it out; the full line box keeps every glyph inside. */
.nx-badge.is-cap-centered {
  padding-block-start: var(--nxt-hairline);
}
/* With an icon the badge lines icon and text up on one row; without one it
   stays inline-block so text overflow inside a badge behaves as before. */
.nx-badge.has-icon {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
}
.nx-badge__icon,
.nx-badge.has-icon :slotted(svg) {
  flex-shrink: 0;
}

.nx-badge.height-xs {
  --nxt-badge-size: var(--nxt-font-size-xs);
}
.nx-badge.height-s {
  --nxt-badge-size: var(--nxt-font-size-sm);
}
.nx-badge.height-m {
  --nxt-badge-size: var(--nxt-font-size-md);
}
.nx-badge.height-l {
  --nxt-badge-size: var(--nxt-font-size-lg);
}
.nx-badge.height-xl {
  --nxt-badge-size: var(--nxt-font-size-xl);
}

/* soft (default): tinted fill + colored text */
.nx-badge.emphasis-soft.variant-default {
  --nxt-badge-bg: var(--nxt-brand-subtle-bg);
  --nxt-badge-fg: var(--nxt-brand-subtle-fg);
  --nxt-badge-border: var(--nxt-brand-border);
}
/* neutral: a quiet grey chip for metadata (no semantic meaning, not brand). */
.nx-badge.emphasis-soft.variant-neutral {
  --nxt-badge-bg: var(--nxt-surface-sunken-bg);
  --nxt-badge-fg: var(--nxt-fg-subtle);
  --nxt-badge-border: var(--nxt-border);
}
.nx-badge.emphasis-soft.variant-success {
  --nxt-badge-bg: var(--nxt-success-subtle-bg);
  --nxt-badge-fg: var(--nxt-success-subtle-fg);
  --nxt-badge-border: var(--nxt-success-border);
}
.nx-badge.emphasis-soft.variant-danger {
  --nxt-badge-bg: var(--nxt-danger-subtle-bg);
  --nxt-badge-fg: var(--nxt-danger-subtle-fg);
  --nxt-badge-border: var(--nxt-danger-border);
}
.nx-badge.emphasis-soft.variant-warning {
  --nxt-badge-bg: var(--nxt-warning-subtle-bg);
  --nxt-badge-fg: var(--nxt-warning-subtle-fg);
  --nxt-badge-border: var(--nxt-warning-border);
}
.nx-badge.emphasis-soft.variant-info {
  --nxt-badge-bg: var(--nxt-info-subtle-bg);
  --nxt-badge-fg: var(--nxt-info-subtle-fg);
  --nxt-badge-border: var(--nxt-info-border);
}

/* solid: full-color fill + on-color text. variant-default fills with the brand
   role (brand bg + on-brand white text), matching the other (fully-filled)
   semantic variants. It deliberately does NOT reuse the info tokens even though
   they currently render alike — info is a distinct semantic role. */
.nx-badge.emphasis-solid.variant-default {
  --nxt-badge-bg: var(--nxt-brand-bg);
  --nxt-badge-fg: var(--nxt-brand-fg);
  --nxt-badge-border: var(--nxt-brand-bg);
}
.nx-badge.emphasis-solid.variant-neutral {
  --nxt-badge-bg: var(--nxt-neutral-bg);
  --nxt-badge-fg: var(--nxt-neutral-fg);
  --nxt-badge-border: var(--nxt-neutral-bg);
}
.nx-badge.emphasis-solid.variant-success {
  --nxt-badge-bg: var(--nxt-success-bg);
  --nxt-badge-fg: var(--nxt-success-fg);
  --nxt-badge-border: var(--nxt-success-bg);
}
.nx-badge.emphasis-solid.variant-danger {
  --nxt-badge-bg: var(--nxt-danger-bg);
  --nxt-badge-fg: var(--nxt-danger-fg);
  --nxt-badge-border: var(--nxt-danger-bg);
}
.nx-badge.emphasis-solid.variant-warning {
  --nxt-badge-bg: var(--nxt-warning-bg);
  --nxt-badge-fg: var(--nxt-warning-fg);
  --nxt-badge-border: var(--nxt-warning-bg);
}
.nx-badge.emphasis-solid.variant-info {
  --nxt-badge-bg: var(--nxt-info-bg);
  --nxt-badge-fg: var(--nxt-info-fg);
  --nxt-badge-border: var(--nxt-info-bg);
}

/* Category hue (NxHue): after the variant rules so it wins at equal specificity. */
.nx-badge.has-hue.emphasis-soft {
  --nxt-badge-bg: var(--hue-subtle-bg);
  --nxt-badge-fg: var(--hue-fg);
  --nxt-badge-border: var(--hue-border);
}
.nx-badge.has-hue.emphasis-solid {
  --nxt-badge-bg: var(--hue);
  --nxt-badge-fg: var(--hue-on);
  --nxt-badge-border: var(--hue);
}
</style>
